import React from "react";

import { referenceUrl } from "../paths";
import { getCachedReference, preloadReference } from "../referenceCache";
import { fetchOptionalJson } from "../utils";

// Loads the committed reference JSON for a marc. No-op in author mode —
// author state is the source of truth there, and layering the saved
// file on top would fight active iteration. Also no-op when marcId is
// null/empty (lets callers conditionally fetch by passing null
// instead of needing a parallel hook tree).
//
// Returns `{ reference, loadedForMarcId }`:
//   reference         — null until the FIRST reference resolves; thereafter
//                       always the most recently loaded reference, even
//                       when marcId is null or while a new fetch is in
//                       flight. That "hold previous" semantic is what
//                       lets the egg-side tween in App stay pinned to
//                       the prior marc's pose during transitional states
//                       (creative pre-pick, speedrun pre-engine-start,
//                       marcId-changed-but-fetch-not-resolved). Without
//                       it, eggSide.eggTransform would snap to
//                       DEFAULT_EGG_TRANSFORM during the gap and the
//                       tween would wobble through a meaningless
//                       intermediate.
//   loadedForMarcId   — the marcId that the currently-returned `reference`
//                       was fetched for. Lets consumers tell "this
//                       reference is in sync with what I asked for"
//                       (loadedForMarcId === marcId) vs "this is the
//                       previous reference, still loading" (mismatch).
//                       The speedrun timer-start gate uses this to know
//                       when the egg has truly arrived at the round's
//                       current marc, not just settled at some pose.
export default function useReferenceJson(marcId, authorMode) {
  // Lazy initial state from the module cache: when App's mount-time
  // prefetch has already populated it (the common case), the first
  // render returns a populated reference and consumers like ScorePanel
  // never see the empty-regions frame.
  const [state, setState] = React.useState(() => {
    if (authorMode || !marcId) {
      return { reference: null, loadedForMarcId: null };
    }
    const cached = getCachedReference(marcId);
    return cached
      ? { reference: cached, loadedForMarcId: marcId }
      : { reference: null, loadedForMarcId: null };
  });
  React.useEffect(() => {
    if (authorMode) return;
    if (!marcId) return;
    const cached = getCachedReference(marcId);
    if (cached) {
      setState({ reference: cached, loadedForMarcId: marcId });
      return;
    }
    let cancelled = false;
    preloadReference(marcId);
    fetchOptionalJson(referenceUrl(marcId)).then((data) => {
      if (!cancelled) {
        setState({ reference: data, loadedForMarcId: marcId });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [authorMode, marcId]);
  return state;
}
